import { useState, useRef } from "react";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { Modal } from "react-bootstrap";
import { BsZoomIn } from "react-icons/bs";
import { toPng } from "html-to-image";

import useGetCompleteStopDetail from "./useGetCompleteStopDetail";
import { FullPageParcelImageModal } from "../../Utility/FullPageParcelImageModal";
import { PartNumberDisplay } from "../../DedicatedDispatch/DedicatedOrderDetails";

function Container({ children }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-h-20">
      {children}
    </div>
  );
}

function SignatureImageModal({ signature, podName, invoice }) {
  const [showModal, setShowModal] = useState(false);
  const podSignatureContainerRef = useRef(null);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const downloadSignature = () => {
    if (!podSignatureContainerRef.current) return;

    toPng(podSignatureContainerRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `Diligent Order Level POD Signature - ${invoice} - Signed by ${podName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        toast.error(`Error downloading signature`);
      });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleShowModal}
        className="tw-unbuttonize tw-w-full tw-relative tw-h-[100px] tw-overflow-hidden tw-rounded-md"
      >
        <img
          className="tw-w-full tw-h-full tw-object-cover tw-transition-all"
          src={`data:image/svg+xml;base64,${signature}`}
          alt="Signature"
        />
        <BsZoomIn className="pod-signature-zoom-icon tw-text-white tw-absolute tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-pointer-events-none tw-transition-all tw-drop-shadow tw-text-xl" />
      </button>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header>
          <Modal.Title>Signature</Modal.Title>
        </Modal.Header>

        <Modal.Body ref={podSignatureContainerRef} className="tw-bg-white">
          <div className="tw-text-center">
            <img
              className="tw-w-[70%]"
              alt="Signature Modal"
              src={`data:image/svg+xml;base64,${signature}`}
            />
            <p>Signed by: {podName || "N/A"}</p>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-primary"
            onClick={downloadSignature}
          >
            Download
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function AttachmentsImgModal({ attachment, invoice }) {
  const [showModal, setShowModal] = useState(false);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  return (
    <div className="tw-pl-2 hover:tw-bg-gray-200">
      <button
        type="button"
        className="tw-unbuttonize tw-block tw-w-full"
        onClick={handleShowModal}
      >
        {attachment}
      </button>

      <FullPageParcelImageModal
        show={showModal}
        handleCloseModal={handleCloseModal}
        selectedImage={attachment}
        invoice={invoice}
      />
    </div>
  );
}

function OrderHistoryDetailPanel({ row }) {
  const {
    data: response,
    isLoading,
    isError,
  } = useGetCompleteStopDetail(row?.original?.positionLinkId);

  const { attachments: attachmentIds } = row?.original;

  const formatAttachment =
    typeof attachmentIds === "string"
      ? attachmentIds
          ?.trim()
          .split(";")
          .filter((item) => item !== "")
      : [];

  if (isLoading) {
    return (
      <Container>
        <Spinner animation="border" role="status" variant="primary">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <div className="tw-text-red-500 tw-font-semibold tw-text-base">
          Error loading detail
        </div>
      </Container>
    );
  }

  if (!response?.data) {
    return (
      <Container>
        <div className="tw-text-base">No detail data available</div>
      </Container>
    );
  }

  return (
    <div className="tw-transition-all tw-ease-in-out tw-px-10 tw-py-5">
      <div className="paper-panel tw-p-8">
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
          <div className="tw-col-span-8">
            <h6>Comments</h6>

            <div className="tw-text-gray-600 tw-text-sm tw-mb-4">
              {row?.original?.notes || "No comments available"}
            </div>
            <PartNumberDisplay cdkOrderId={row?.original?.cdkOrderId} />
          </div>

          <div className="tw-col-span-4 tw-h-fit tw-min-h-[300px] tw-shadow-[0px_4px_4px_rgba(0,0,0,0.15),0px_4px_12px_rgba(0,0,0,0.15)] tw-p-5 tw-rounded-2xl">
            <h6 className="tw-border-b tw-border-solid tw-border-t-0 tw-border-x-0 tw-border-gray-200">
              Images
            </h6>

            {formatAttachment.length > 0 ? (
              formatAttachment.map((attachment) => (
                <AttachmentsImgModal
                  key={attachment}
                  attachment={attachment}
                  invoice={row?.original?.invoiceNumber}
                />
              ))
            ) : (
              <div className="tw-text-gray-500 tw-text-sm">
                No images available
              </div>
            )}

            <h6 className="tw-border-b tw-border-solid tw-border-t-0 tw-border-x-0 tw-border-gray-200">
              Proof of Delivery:
            </h6>
            <div>Signed by: {row?.original?.pod}</div>
            <div>Signature:</div>

            <div>
              {row?.original?.signature &&
              row?.original?.signature.trim() !== "" ? (
                <SignatureImageModal
                  signature={row?.original?.signature}
                  podName={row?.original?.pod}
                  invoice={row?.original?.invoiceNumber}
                />
              ) : (
                <div className="tw-text-gray-500 tw-text-sm">
                  No signature available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderHistoryDetailPanel;
